<script lang="ts">
  import { Icon } from '$ui/primitives/Icon';

  let {
    value = $bindable<string | null>(null),
    options = [],
    placeholder = 'Select...',
    disabled = false,
    size = 'sm',
    onchange,
    class: className = ''
  }: {
    value?: string | null;
    options: Array<{ value: string; label: string }>;
    placeholder?: string;
    disabled?: boolean;
    size?: 'sm' | 'md';
    onchange?: (value: string | null) => void;
    class?: string;
  } = $props();

  let isOpen = $state(false);
  let searchQuery = $state('');
  let container: HTMLDivElement;

  let selectedLabel = $derived(options.find((o) => o.value === value)?.label || placeholder);

  let filteredOptions = $derived(
    options.filter((o) => o.label.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  function toggle() {
    if (disabled) return;
    isOpen = !isOpen;
    if (isOpen) {
      searchQuery = '';
      setTimeout(() => {
        const input = container.querySelector('input');
        if (input) input.focus();
      }, 10);
    }
  }

  function select(opt: { value: string; label: string }) {
    value = opt.value;
    onchange?.(opt.value);
    isOpen = false;
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      isOpen = false;
    }
  }

  function handleClickOutside(e: MouseEvent) {
    if (isOpen && container && !container.contains(e.target as Node)) {
      isOpen = false;
    }
  }
</script>

<svelte:window onclick={handleClickOutside} onkeydown={handleKeydown} />

<div class="search-select {className}" bind:this={container} class:disabled>
  <button type="button" class="trigger" data-size={size} onclick={toggle} {disabled}>
    <span class="label" class:placeholder={!value}>{selectedLabel}</span>
    <span class="chevron"><Icon name="chevron-down" size={16} /></span>
  </button>

  {#if isOpen}
    <div class="dropdown">
      <div class="search-wrap">
        <Icon name="search" size={14} />
        <input type="text" bind:value={searchQuery} placeholder="Search..." />
      </div>
      <ul class="options">
        {#if filteredOptions.length === 0}
          <li class="empty">No results</li>
        {:else}
          {#each filteredOptions as opt (opt.value)}
            <li>
              <button type="button" class:active={opt.value === value} onclick={() => select(opt)}>
                {opt.label}
              </button>
            </li>
          {/each}
        {/if}
      </ul>
    </div>
  {/if}
</div>

<style>
  .search-select {
    position: relative;
    width: 100%;
  }
  .trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-full);
    font-family: var(--font-sans);
    color: var(--color-text-primary);
    cursor: pointer;
    transition: border-color var(--dur-fast) var(--ease-out);
  }
  .trigger[data-size='sm'] {
    height: 32px;
    padding: 0 var(--space-3);
    font-size: var(--text-sm);
  }
  .trigger:hover:not(:disabled) {
    border-color: var(--color-text-faint);
  }
  .trigger:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    border-color: var(--color-accent);
  }
  .trigger:disabled {
    cursor: not-allowed;
    color: var(--color-text-muted);
    opacity: 0.6;
  }
  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .label.placeholder {
    color: var(--color-text-muted);
  }
  .chevron {
    color: var(--color-text-muted);
    display: flex;
    align-items: center;
    margin-left: var(--space-2);
  }
  .dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    width: 100%;
    min-width: 220px;
    background: var(--color-surface-elevated);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    z-index: var(--z-popover);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .search-wrap {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    border-bottom: var(--border-width) solid var(--color-border);
    color: var(--color-text-muted);
  }
  .search-wrap input {
    flex: 1;
    border: none;
    background: transparent;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    outline: none;
  }
  .options {
    list-style: none;
    margin: 0;
    padding: var(--space-1) 0;
    max-height: 200px;
    overflow-y: auto;
  }
  .options li button {
    width: 100%;
    text-align: left;
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: none;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    color: var(--color-text-primary);
    cursor: pointer;
  }
  .options li button:hover {
    background: var(--color-surface-hover);
  }
  .options li button.active {
    background: var(--color-accent-tint);
    color: var(--color-accent);
    font-weight: var(--weight-medium);
  }
  .empty {
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }
</style>